import { format, UTCDate } from '@/utils/dates';
import { ColumnDef } from '@tanstack/react-table';
import { Info, InfoIcon } from 'lucide-react';
import { AmountDisplay } from '../../../../design-system/amount-display/amount-display';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Tooltip } from '../../../../design-system/tooltip/tooltip';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { cn } from '../../../../utils/cn';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { formatMomChange } from '../../utils/calculate-monthly-breakdown';
import { ReportingData } from '../../utils/get-reporting-data';
import { ExpandableNameCell } from './expandable-name-cell';
import { ExpansionChevronIconButton } from './expansion-chevron-icon-button';

// Helper component to display amounts with proper sign logic for reporting table
// Revenue (positive): shows + sign
// Expenses (negative): shows - sign (via isAccountBalance)
// Zeros: shows "0" (via isAccountBalance)
// eslint-disable-next-line react-refresh/only-export-components
const ReportingAmountDisplay = ({
  normalizedAmount,
  currency,
  className,
}: {
  normalizedAmount: number;
  currency?: SupportedCountryCurrency;
  className?: string;
}) => {
  // Use isAccountBalance=true to show zeros as "0" and negatives with "-" sign
  // For positive amounts, get the formatted string and prepend "+" directly to match minus sign spacing
  if (normalizedAmount > 0) {
    const formattedAmount = getAmountWithCurrency({
      amount: normalizedAmount,
      currency,
      isAccountBalance: true,
    });
    return <p className={cn('text-sm text-nowrap text-success tabular-nums', className)}>+{formattedAmount}</p>;
  }

  // For zero and negative amounts, use AmountDisplay directly
  return (
    <AmountDisplay
      normalizedAmount={normalizedAmount}
      currency={currency}
      isAccountBalance={true}
      className={className}
    />
  );
};

export const reportingTableColumnsDefinition: ColumnDef<ReportingData>[] = [
  {
    accessorKey: 'name',
    header: ({ table }) => {
      return (
        <Inline space={3}>
          <ExpansionChevronIconButton
            isExpanded={table.getIsAllRowsExpanded()}
            onClick={table.getToggleAllRowsExpandedHandler()}
          />

          <p>Name</p>
        </Inline>
      );
    },
    cell: ({ row }) => (
      // Min width to prevent layout shift when user toggles row visibility
      <Inline wrap="nowrap">
        <ExpandableNameCell row={row} />

        {row.original.hierarchyLevel === 'VENDOR' && row.original.description && (
          <Tooltip
            trigger={<Info className="h-3 w-3 cursor-pointer" />}
            content={row.original.description}
            contentClassName="text-xs px-3 py-2 w-52"
          />
        )}
      </Inline>
    ),
    footer: 'Net Cash Movement',
    size: 24,
  },
  {
    id: 'actions',
    cell: ({ row, table }) => {
      const entityType = row.original.hierarchyLevel;

      const rowId = row.original.id;
      const parentRowId = row.getParentRow()?.id;

      if (!rowId || !parentRowId) {
        return <div className="w-24"></div>;
      }

      switch (entityType) {
        case 'NO_CATEGORY':
        case 'LEVEL_1_CATEGORY':
          // Keeps the space in rows without the button so there is no layout shift when user toggles row visibility
          return <div className="w-24"></div>;
        case 'LEVEL_2_CATEGORY':
          return (
            <Inline justify="center" className="w-24 opacity-0 transition-opacity group-hover/row:opacity-100">
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  table.options.meta?.onMoveCategory?.({
                    id: rowId,
                    parentRowId,
                    level1: row.getParentRow()?.original.name,
                  });
                }}
                className="py-0"
              >
                Move
              </Button>
            </Inline>
          );
        case 'VENDOR':
          return (
            <Inline justify="center" className="w-24 opacity-0 transition-opacity group-hover/row:opacity-100">
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  table.options.meta?.onMoveVendor?.({ vendorId: rowId, parentRowId });
                }}
                className="py-0"
              >
                Move
              </Button>
            </Inline>
          );
        default:
          return exhaustiveCheck(entityType);
      }
    },
    size: 10,
    minSize: 10,
  },
  {
    accessorKey: 'three_months_ago_amount',
    accessorFn: (row) => row.amountBreakdown.threeMonthsAgoAmount,
    // Month name removed - displayed in chart instead
    header: () => '',
    cell: ({ row }) => (
      <ReportingAmountDisplay
        normalizedAmount={row.original.amountBreakdown.threeMonthsAgoAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <ReportingAmountDisplay
        normalizedAmount={table.options.meta?.totals?.threeMonthsAgoAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    accessorKey: 'two_months_ago_amount',
    accessorFn: (row) => row.amountBreakdown.twoMonthsAgoAmount,
    // Month name removed - displayed in chart instead
    header: () => '',
    cell: ({ row }) => (
      <ReportingAmountDisplay
        normalizedAmount={row.original.amountBreakdown.twoMonthsAgoAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <ReportingAmountDisplay
        normalizedAmount={table.options.meta?.totals?.twoMonthsAgoAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    accessorKey: 'previous_month_amount',
    accessorFn: (row) => row.amountBreakdown.previousMonthAmount,
    // Month name removed - displayed in chart instead
    header: () => '',
    cell: ({ row }) => (
      <ReportingAmountDisplay
        normalizedAmount={row.original.amountBreakdown.previousMonthAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <ReportingAmountDisplay
        normalizedAmount={table.options.meta?.totals?.previousMonthAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    id: 'current_month_amount',
    accessorFn: (row) => row.amountBreakdown.currentMonthAmount,
    // Month name removed - displayed in chart instead
    header: () => '',
    cell: ({ row }) => (
      <ReportingAmountDisplay
        normalizedAmount={row.original.amountBreakdown.currentMonthAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <ReportingAmountDisplay
        normalizedAmount={table.options.meta?.totals?.currentMonthAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
  {
    accessorKey: 'month_over_month_increase',
    accessorFn: (row) => row.amountBreakdown.monthOverMonthIncrease,
    header: ({ table }) => {
      const isCurrentMonth = table.options.meta?.isCurrentMonth ?? false;
      const month1 = table.options.meta?.month1 ?? new UTCDate();
      const month2 = table.options.meta?.month2 ?? new UTCDate();
      const month0 = table.options.meta?.month0 ?? new UTCDate();

      return (
        <Tooltip
          trigger={
            <span className="inline-flex cursor-help items-center justify-center gap-1">
              <p>Trend</p>
              <InfoIcon className="h-3 w-3" />
            </span>
          }
          content={`% change, ${format(isCurrentMonth ? month1 : month0, 'MMMM')} vs ${format(
            isCurrentMonth ? month2 : month1,
            'MMMM',
          )}`}
        />
      );
    },
    cell: ({ row, table }) => {
      const isCurrentMonth = table.options.meta?.isCurrentMonth ?? false;
      const momData = formatMomChange(
        row.original.amountBreakdown.monthOverMonthIncrease,
        isCurrentMonth
          ? row.original.amountBreakdown.twoMonthsAgoAmount
          : row.original.amountBreakdown.previousMonthAmount,
      );

      const ArrowIcon = momData.arrow;
      return (
        <div className={`min-w-24 text-right ${momData.colorClass}`}>
          <span className="flex items-center justify-end gap-1">
            {momData.sign}
            {momData.percentageChange}% <ArrowIcon size={16} strokeWidth={2} />
          </span>
        </div>
      );
    },
    footer: ({ table }) => {
      const isCurrentMonth = table.options.meta?.isCurrentMonth ?? false;
      const momData = formatMomChange(
        table.options.meta?.totals?.monthOverMonthIncrease ?? 0,
        isCurrentMonth
          ? (table.options.meta?.totals?.twoMonthsAgoAmount ?? 0)
          : (table.options.meta?.totals?.previousMonthAmount ?? 0),
      );

      const ArrowIcon = momData.arrow;
      return (
        <div className={`min-w-24 text-right ${momData.colorClass}`}>
          <span className="flex items-center justify-end gap-1">
            {momData.sign}
            {momData.percentageChange}% <ArrowIcon size={16} strokeWidth={2} />
          </span>
        </div>
      );
    },
    size: 11,
    minSize: 11,
  },
  {
    accessorKey: 'totalAmount',
    header: () => 'Total',
    cell: ({ row }) => (
      <ReportingAmountDisplay
        normalizedAmount={row.original.amountBreakdown.totalAmount}
        currency={row.original.amountBreakdown.currency}
        className="min-w-24"
      />
    ),
    footer: ({ table }) => (
      <ReportingAmountDisplay
        normalizedAmount={table.options.meta?.totals?.totalAmount ?? 0}
        currency={table.options.meta?.totals?.currency}
        className="min-w-24"
      />
    ),
    size: 11,
    minSize: 11,
  },
];
